/**
 * AssignedOrgsSheet — right-side drill-down panel listing every org
 * tagged to a given industry (or the synthetic ``_unassigned`` bucket).
 * Each row has an inline "Reassign to…" Select for one-click moves.
 */
import React, { useCallback, useEffect, useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import { toast } from 'sonner';
import { Users, Loader2 } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from './helpers';

export default function AssignedOrgsSheet({ open, industry, onClose, profiles, onReassigned }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reassigning, setReassigning] = useState(null); // org_id being moved

  const industryId = industry?.industry_id;
  const industryName = industry?.industry_name || (industryId === '_unassigned' ? 'Unassigned' : industryId);

  const load = useCallback(async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/${encodeURIComponent(industryId)}/orgs`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOrgs(data?.organizations || []);
    } catch (e) {
      toast.error(`Failed to load orgs: ${e.message || e}`);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [industryId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const reassign = async (org, toIndustryId) => {
    if (!toIndustryId || toIndustryId === industryId) return;
    setReassigning(org.id);
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/reassign-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: org.id, to_industry_id: toIndustryId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText);
      }
      const newLabel = toIndustryId === '_unassigned'
        ? 'Unassigned'
        : (profiles.find((p) => p.id === toIndustryId)?.name || toIndustryId);
      toast.success(`Moved "${org.name}" → ${newLabel}`);
      // Optimistic: drop the row locally so the panel responds instantly,
      // then refresh parent analytics so card totals update.
      setOrgs((prev) => prev.filter((o) => o.id !== org.id));
      await onReassigned?.();
    } catch (e) {
      toast.error(`Reassign failed: ${e.message || e}`);
    } finally {
      setReassigning(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="assigned-orgs-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-600" />
            Orgs in <span className="text-indigo-700">{industryName}</span>
          </SheetTitle>
          <SheetDescription>
            {industryId === '_unassigned'
              ? 'Organizations with no industry tag yet — assign one to enable industry-specific ROI math + AI tone.'
              : 'Click "Reassign to…" to move a misclassified org to a different vertical without leaving this view.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {loading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading orgs…
            </div>
          ) : orgs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500" data-testid="assigned-orgs-empty">
              No organizations assigned to this industry.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="assigned-orgs-list">
              {orgs.map((org) => (
                <li
                  key={org.id}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                  data-testid={`assigned-org-row-${org.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{org.name}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500 font-mono truncate">{org.id}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3 text-slate-400" />
                          {org.headcount >= 1000 ? `${(org.headcount / 1000).toFixed(1)}k` : org.headcount} HC
                        </span>
                        {org.user_count > 0 && (
                          <span className="text-slate-400">· {org.user_count} users</span>
                        )}
                        {org.updated_at && (
                          <span className="text-slate-400">
                            · last update {new Date(org.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {reassigning === org.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-600 mt-1" />
                    ) : (
                      <Select onValueChange={(v) => reassign(org, v)} data-testid={`reassign-${org.id}`}>
                        <SelectTrigger
                          className="h-8 w-[150px] text-xs"
                          data-testid={`reassign-trigger-${org.id}`}
                        >
                          <SelectValue placeholder="Reassign to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {profiles
                            .filter((p) => p.published !== false && p.id !== industryId)
                            .map((p) => (
                              <SelectItem key={p.id} value={p.id} data-testid={`reassign-option-${org.id}-${p.id}`}>
                                {p.name}
                              </SelectItem>
                            ))}
                          {industryId !== '_unassigned' && (
                            <SelectItem value="_unassigned" data-testid={`reassign-option-${org.id}-unassigned`}>
                              — Clear (unassigned)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
